import React, { useEffect, useMemo, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useNotify } from 'react-admin'
import useQueryParams from '../../../hooks/useQueryParams'
import useGetLocalStorage from '../../../hooks/useGetLocalStorage'
import QueryKeys from '../../../utils/constants/queryKeys'
import localStorageIds from '../../../utils/constants/localStorageIds'
import USER_ORG_ROLES from '../../../utils/constants/userOrgRoles'
import USER_PERMISSIONS from '../../../utils/constants/userPermissions'
import { dutiesForOrgType, toGrants, toggledDuties } from '../../../utils/constants/permissionSets'
import ORGANISATION_TYPES from '../../../utils/constants/OrganisationTypes'
import getCompetitions from '../../../Apis/competition/getCompetitions'
import hasPermission from '../../../utils/helpers/hasPermission'
import getOrgUsers from '../../../Apis/organisation/getOrgUsers'
import updateOrgUser from '../../../Apis/organisation/updateOrgUser'
import deleteUserFromOrg from '../../../Apis/organisation/deleteUserFromOrg'
import getOrgPendingInvitations from '../../../Apis/invitations/getOrgPendingInvitations'
import updateInvitation from '../../../Apis/invitations/updateInvitation'
import deleteInvitation from '../../../Apis/invitations/deleteInvitation'
import resendInvitation from '../../../Apis/invitations/resendInvitation'
import getUserData from '../../../Apis/user/getUserData'
import LoaderDots from '../../../components/loaders/LoaderDots/LoaderDots'
import CardWrapperMoreIcon from '../../../components/layout/CardWrapperMoreIcon/CardWrapperMoreIcon'
import InviteDialog from '../InviteDialog/InviteDialog'
import OrgUserRow from '../OrgUserRow/OrgUserRow'
import { PAGE_SIZE, buildRows, filterRows, pageOf } from './orgUsersModel'
import orgUsersPageStyles from './orgUsersPage.styles'

const useStyles = makeStyles(orgUsersPageStyles)

const SEARCH_DELAY_MS = 250

const errorMessage = (err, fallback) =>
  err?.response?.data?.details || err?.response?.data?.message || fallback

/**
 * The org's people: members with their access (Owner, Admin, Member) and a Member's duties, then
 * pending invitations. Changes save as they are made; what the caller may change comes from their
 * role and the server's permissions.
 */
const OrgUsersPage = () => {
  const classes = useStyles()
  const notify = useNotify()
  const queryClient = useQueryClient()
  const { org: orgId } = useQueryParams()
  const storage = useGetLocalStorage() || {}
  const orgType = storage[localStorageIds.SELECTED_ORG]?.type
  const callerRole = storage[localStorageIds.USER_ORG_ROLES]?.[orgId]

  const [query, setQuery] = useState('')
  const [searchedQuery, setSearchedQuery] = useState('')
  const [page, setPage] = useState(0)
  const listTop = useRef(null)
  const [duty, setDuty] = useState('')
  const [adminsOnly, setAdminsOnly] = useState(false)
  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const [toRemove, setToRemove] = useState(null)
  const [busyKey, setBusyKey] = useState(null)
  // Rows left without a duty while one is being picked (the last duty un-ticked, or an Admin made a
  // Member): shown as edited but not saved, since a Member must hold a duty. Picking one saves.
  const [drafts, setDrafts] = useState({})
  const setDraft = (key, draft) =>
    setDrafts(({ [key]: _dropped, ...rest }) => (draft ? { ...rest, [key]: draft } : rest))

  const me = useQuery([QueryKeys.GET_ACCOUNT_ME], () => getUserData())
  const usersQuery = useQuery([QueryKeys.GET_ORG_USERS, orgId], () => getOrgUsers(orgId), { enabled: Boolean(orgId) })
  const invitationsQuery = useQuery(
    [QueryKeys.GET_ORG_PENDING_INVITATIONS, orgId],
    () => getOrgPendingInvitations({ orgId }),
    { enabled: Boolean(orgId) },
  )

  const duties = dutiesForOrgType(orgType)
  const isFederation = orgType === ORGANISATION_TYPES.ORG_FEDERATION
  const competitionsQuery = useQuery([QueryKeys.GET_COMPETITIONS, orgId], () => getCompetitions(orgId), {
    enabled: Boolean(orgId) && isFederation,
  })
  const competitions = isFederation ? competitionsQuery.data || [] : []
  const storedPermissions = localStorage.getItem(localStorageIds.ORG_PERMISSIONS)
  // buildRows asks this several times per row, and hasPermission parses localStorage each call:
  // answer each permission once per org and stored permissions.
  const can = useMemo(() => {
    const answers = {}
    return (permission) => {
      if (!(permission in answers)) answers[permission] = hasPermission(permission, [orgId])
      return answers[permission]
    }
  }, [orgId, storedPermissions])

  // `storage` is a new object every render, so it must not be a dependency: a federation's ~1,500
  // rows would be rebuilt on every keystroke.
  const { members, pending } = useMemo(
    () => buildRows({ users: usersQuery.data, invitations: invitationsQuery.data, orgId, callerRole, can }),
    [usersQuery.data, invitationsQuery.data, orgId, callerRole, can],
  )
  useEffect(() => {
    const timer = setTimeout(() => setSearchedQuery(query), SEARCH_DELAY_MS)
    return () => clearTimeout(timer)
  }, [query])
  useEffect(() => setPage(0), [searchedQuery, duty, adminsOnly])

  const goToPage = (next) => {
    setPage(next)
    listTop.current?.scrollIntoView({ block: 'start' })
  }

  const matches = useMemo(
    () => {
      const filters = { query: searchedQuery, duty, admins: adminsOnly }
      return [...filterRows(members, filters), ...filterRows(pending, filters)]
    },
    [members, pending, searchedQuery, duty, adminsOnly],
  )
  const shown = pageOf(matches, page)
  const shownMembers = shown.rows.filter((row) => !row.pending)
  const shownPending = shown.rows.filter((row) => row.pending)

  const refresh = () => {
    queryClient.invalidateQueries(QueryKeys.GET_ORG_USERS)
    queryClient.invalidateQueries(QueryKeys.GET_ORG_PENDING_INVITATIONS)
  }

  const change = useMutation(
    ({ row, body }) =>
      row.pending
        ? updateInvitation({ orgId, invitationId: row.id, requestBody: body })
        : updateOrgUser({ orgId, requestBody: { userId: row.id, ...body } }),
    {
      onMutate: ({ row }) => setBusyKey(row.key),
      onSuccess: refresh,
      onError: (err) => notify(errorMessage(err, 'Could not save the change'), 'warning'),
      onSettled: () => setBusyKey(null),
    },
  )

  const remove = useMutation(
    (row) =>
      row.pending ? deleteInvitation({ orgId, invitationId: row.id }) : deleteUserFromOrg({ orgId, userId: row.id }),
    {
      onSuccess: () => {
        setToRemove(null)
        refresh()
      },
      onError: (err) => notify(errorMessage(err, 'Could not remove them'), 'warning'),
    },
  )

  const resend = useMutation((row) => resendInvitation({ orgId, invitationId: row.id }), {
    onMutate: (row) => setBusyKey(row.key),
    onSuccess: (_data, row) => notify(`Invitation sent again to ${row.email}`),
    onError: (err) => notify(errorMessage(err, 'Could not resend the invitation'), 'warning'),
    onSettled: () => setBusyKey(null),
  })

  // Duties are a Member's only, so the Admins filter and a duty filter cannot both apply.
  const toggleAdminsOnly = () => {
    setAdminsOnly((on) => !on)
    setDuty('')
  }

  const save = (row, body) =>
    change.mutate({ row, body }, { onSuccess: () => setDraft(row.key, null) })

  // A Member gets everything through their duties: un-ticking the last one leaves the row waiting for
  // another instead of saving it empty. A Manager keeps the competitions they are limited to while
  // other duties change; un-ticking Manager drops them.
  const toggleDuty = (row, dutyId) => {
    const next = toggledDuties(row.duties, dutyId)
    if (next.length === 0) return setDraft(row.key, { level: row.level, roleChanged: !!drafts[row.key]?.roleChanged })
    const grants = toGrants(next, row.managerCompetitions)
    save(row, drafts[row.key]?.roleChanged ? { role: row.level, grants } : { grants })
  }

  const changeCompetitions = (row, competitionIds) => save(row, { grants: toGrants(row.duties, competitionIds) })

  // An Admin made a Member picks their duties first: the role is saved with the first one.
  const changeAccess = (row, role) => {
    const saved = row.savedLevel ?? row.level
    if (role === saved) return setDraft(row.key, null)
    if (role === USER_ORG_ROLES.MEMBER) return setDraft(row.key, { level: role, roleChanged: true })
    return save(row, { role })
  }

  const withDraft = (row) =>
    drafts[row.key]
      ? {
          ...row,
          savedLevel: row.level,
          level: drafts[row.key].level,
          duties: [],
          managerCompetitions: [],
          draft: true,
          // Whoever may make them a Member may pick that Member's duties.
          canEditDuties: row.canEditDuties || row.canEditAccess,
        }
      : row

  const rowProps = (listed) => {
    const row = withDraft(listed)
    return {
      row,
      duties,
      classes,
      busy: busyKey === row.key,
      onAccessChange: (role) => changeAccess(row, role),
      onToggleDuty: (dutyId) => toggleDuty(row, dutyId),
      competitions,
      onCompetitionsChange: (competitionIds) => changeCompetitions(row, competitionIds),
      confirmingRemove: toRemove?.key === row.key,
      removing: remove.isLoading && toRemove?.key === row.key,
      onRemove: () => setToRemove(row),
      onConfirmRemove: () => remove.mutate(row),
      onCancelRemove: () => setToRemove(null),
      onResend: () => resend.mutate(row),
    }
  }

  const canInvite = can(USER_PERMISSIONS.INVITE)
  const invitableRoles =
    callerRole === USER_ORG_ROLES.OWNER ? [USER_ORG_ROLES.MEMBER, USER_ORG_ROLES.ADMIN] : [USER_ORG_ROLES.MEMBER]
  const existingEmails = [...members, ...pending].map((row) => (row.email || '').toLowerCase())
  const isLoading = usersQuery.isLoading || invitationsQuery.isLoading
  const nothingShown = matches.length === 0
  const firstShown = shown.page * PAGE_SIZE + 1
  const lastShown = shown.page * PAGE_SIZE + shown.rows.length

  return (
    <>
      {isInviteOpen && (
        <InviteDialog
          orgId={orgId}
          duties={duties}
          competitions={competitions}
          invitableRoles={invitableRoles}
          existingEmails={existingEmails}
          selfEmail={me.data?.email}
          onClose={() => setIsInviteOpen(false)}
        />
      )}

      <CardWrapperMoreIcon>
        <div ref={listTop} className={classes.usersCard} style={{ background: '#ffffff', border: '1px solid #e8e8e8', borderRadius: 4, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', color: '#212121' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16, paddingBottom: 24, borderBottom: '1px solid #e0e0e0' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 500, color: '#212121' }}>Organisation Users</h1>
              <p style={{ margin: 0, fontSize: 15, color: '#616161' }}>Manage people, access and invitations</p>
            </div>
            {canInvite && (
              <button type='button' className={classes.inviteButton} onClick={() => setIsInviteOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, height: 44, padding: '0 20px', border: 0, borderRadius: 4, background: '#c9453f', color: '#ffffff', fontSize: 15, fontWeight: 500, cursor: 'pointer' }}>
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'><rect x='3' y='5' width='18' height='14' rx='2' /><path d='M3 7l9 6 9-6' /></svg>
                Invite
              </button>
            )}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '20px 0 8px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, flex: '1 1 auto', minWidth: 0 }}>
              <label className={classes.search} style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 260px', maxWidth: 360, height: 44, padding: '0 14px', boxSizing: 'border-box', border: '1px solid #d6d6d6', borderRadius: 4, background: '#ffffff', color: '#757575' }}>
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' aria-hidden='true'><circle cx='11' cy='11' r='7' /><path d='M20 20l-3.5-3.5' /></svg>
                <input type='search' aria-label='Search users' placeholder='Search by name or email' value={query} onChange={(e) => setQuery(e.target.value)} style={{ flexGrow: 1, minWidth: 0, border: 0, background: 'transparent', fontSize: 15, color: '#212121', height: 40, fontFamily: 'inherit' }} />
              </label>
              <button type='button' className={classes.chip} aria-pressed={adminsOnly} onClick={toggleAdminsOnly} style={{ display: 'flex', alignItems: 'center', gap: 6, height: 44, padding: '0 16px', borderRadius: 4, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit', ...(adminsOnly ? { border: '1px solid #e8b4b1', background: '#fdeceb', color: '#9e322d' } : { border: '1px solid #d6d6d6', background: '#ffffff', color: '#212121' }) }}>
                Admins
              </button>
              {duties.length > 0 && (
                <select className={`${classes.select} ${classes.accessSelect}`} aria-label='Filter by duty' value={duty} disabled={adminsOnly} onChange={(e) => setDuty(e.target.value)} style={{ width: 170, height: 44, padding: '0 32px 0 12px', boxSizing: 'border-box', border: '1px solid #d6d6d6', borderRadius: 4, backgroundColor: adminsOnly ? '#f5f5f5' : '#ffffff', fontSize: 15, color: adminsOnly ? '#9e9e9e' : '#212121', cursor: adminsOnly ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                  <option value=''>All duties</option>
                  {duties.map((d) => (
                    <option key={d.id} value={d.id}>{d.label}</option>
                  ))}
                </select>
              )}
            </div>
            <div style={{ fontSize: 13, color: '#616161' }}>
              {`${members.length} members · ${pending.length} pending`}
            </div>
          </div>

          <div className={`${classes.grid} ${classes.headerRow}`} style={{ padding: '14px 16px 12px', borderBottom: '1px solid #e0e0e0', fontSize: 13, fontWeight: 700, color: '#212121' }}>
            <div className={classes.memberCell}>Member</div>
            <div className={classes.accessCell}>Access</div>
            <div className={classes.dutiesCell}>
              Duties <span style={{ fontWeight: 400, color: '#6b6b6b' }}>· members only</span>
            </div>
          </div>

          {isLoading ? (
            <LoaderDots style={orgUsersPageStyles.loaderIcon} />
          ) : (
            <>
              {shownMembers.map((row) => <OrgUserRow key={row.key} {...rowProps(row)} />)}

              {shownPending.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '32px 16px 12px', borderBottom: '1px solid #e0e0e0' }}>
                  <h2 style={{ margin: 0, fontSize: 16, fontWeight: 500, color: '#212121' }}>Pending invitations</h2>
                  <span style={{ fontSize: 13, color: '#616161' }}>Access and duties apply once accepted</span>
                </div>
              )}
              {shownPending.map((row) => <OrgUserRow key={row.key} {...rowProps(row)} />)}

              {shown.lastPage > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, padding: '16px 16px 4px', fontSize: 13, color: '#616161' }}>
                  <span>{`${firstShown}–${lastShown} of ${matches.length}`}</span>
                  <button type='button' className={classes.iconButton} aria-label='Previous page' disabled={shown.page === 0} onClick={() => goToPage(shown.page - 1)} style={{ height: 32, padding: '0 12px', border: '1px solid #d6d6d6', borderRadius: 4, background: 'transparent', fontSize: 13, color: shown.page === 0 ? '#bdbdbd' : '#212121', cursor: shown.page === 0 ? 'default' : 'pointer' }}>
                    Previous
                  </button>
                  <button type='button' className={classes.iconButton} aria-label='Next page' disabled={shown.page === shown.lastPage} onClick={() => goToPage(shown.page + 1)} style={{ height: 32, padding: '0 12px', border: '1px solid #d6d6d6', borderRadius: 4, background: 'transparent', fontSize: 13, color: shown.page === shown.lastPage ? '#bdbdbd' : '#212121', cursor: shown.page === shown.lastPage ? 'default' : 'pointer' }}>
                    Next
                  </button>
                </div>
              )}

              {nothingShown && (
                <div style={{ padding: '48px 16px', textAlign: 'center', fontSize: 15, color: '#616161' }}>
                  No users match your search or filter
                </div>
              )}
            </>
          )}
        </div>
      </CardWrapperMoreIcon>
    </>
  )
}

export default OrgUsersPage
